import { Box, Grid } from '@mui/material';
import * as React from 'react';
import { httpGet } from '../../HttpRequestHandling/httpRequestHandler';

const CALL_LETTER_MANUAL = '__manual__';

export function MediaDetailsFields(props) {

	const [mediaTypeOptions, setMediaTypeOptions] = React.useState([]);
	const [mediaTypeBandMap, setMediaTypeBandMap] = React.useState({});
	const [filteredBandOptions, setFilteredBandOptions] = React.useState([]);
	const [callLetterManualMode, setCallLetterManualMode] = React.useState(false);

	React.useEffect(() => {
		if (!props.companyCode || props.modelDefId !== 3) return;

		httpGet(`/api/invoicedocument/mediatypebands/${props.companyCode}`)
			.then((response) => {
				const data = response.data;
				const mediaMap = {};
				const mediaList = [];

				data.forEach(item => {
					if (!mediaMap[item.mediaTypeCode]) {
						mediaMap[item.mediaTypeCode] = [];
						mediaList.push({
							value: item.mediaTypeCode,
							label: item.mediaTypeName
						});
					}
					mediaMap[item.mediaTypeCode].push(item.mediaTypeBand);
				});

				setMediaTypeOptions(mediaList);
				setMediaTypeBandMap(mediaMap);

				const selectedMediaType = props.mappedFields['mediaType']?.value;
				const allBands = Array.from(new Set(data.map(item => item.mediaTypeBand)));
				const bandDropdown = (bands) => [{ value: '', label: 'Select Band' }, ...bands.map(b => ({ value: b, label: b }))];

				if (selectedMediaType && mediaMap[selectedMediaType]) {
					setFilteredBandOptions(bandDropdown(mediaMap[selectedMediaType]));
				} else {
					setFilteredBandOptions(bandDropdown(allBands));
				}

			})
			.catch(() => toast.error("Failed to load media type and band options"));
	}, [props.companyCode]);

	const sortByKey = (arr) => {
		const seen = new Set();
		return [...(arr || [])]
			.filter((item) => {
				const normalizedKey = (item?.Key ?? '').toString().trim().toLowerCase();
				if (seen.has(normalizedKey)) return false;
				seen.add(normalizedKey);
				return true;
			})
			.sort((a, b) => (a.Key || '').toString().localeCompare((b.Key || '').toString(), undefined, { sensitivity: 'base', numeric: true }));
	};
	const sortedKeyValues = React.useMemo(() => sortByKey(props.fieldData?.KeyValues), [props.fieldData?.KeyValues]);

	// Key shown in the dropdown — predefined uses the value itself; key-value uses the key name
	const bandSelectValue =
		props.mappedFields['band']?.value ||
		props.mappedFields['bandKeyValue']?.mappedField ||
		'';

	// Actual resolved value shown in the read-only input next to the band dropdown
	const bandDisplayValue =
		props.mappedFields['band']?.value ||
		props.mappedFields['bandKeyValue']?.value ||
		'';

	const callLetterManual = callLetterManualMode || (!!props.mappedFields['callLetter']?.value && !props.mappedFields['callLetterKeyValue']?.mappedField);

	// Key shown in the call letter dropdown
	const callLetterSelectValue = props.mappedFields['callLetterKeyValue']?.mappedField || (callLetterManual ? CALL_LETTER_MANUAL : '');

	// Actual resolved value shown in the call letter input
	const callLetterDisplayValue =
		props.mappedFields['callLetter']?.value ||
		props.mappedFields['callLetterKeyValue']?.value ||
		'';

	const handleChange = (field) => (event) => {
		const value = event.target.value;

		if (field === 'mediaType') {
			const allBands = Array.from(new Set(Object.values(mediaTypeBandMap).flat()));
			const bands = value ? mediaTypeBandMap[value] || [] : allBands;
			setFilteredBandOptions([{ value: '', label: 'Select Band' }, ...bands.map(b => ({ value: b, label: b }))]);

			// Clear both band fields when media type changes
			props.field_changehandler({ event: { target: { value: '' } }, value: 'band' });
			props.field_changehandler({ event: { target: { value: '' } }, value: 'bandKeyValue' });
		}

		props.field_changehandler({
			event: { target: { value: value } },
			value: field
		});
	};

	// Grouped band dropdown handler
	const handleBandChange = (event) => {
		const value = event.target.value;

		if (!value) {
			props.field_changehandler({ event: { target: { value: '' } }, value: 'band' });
			props.field_changehandler({ event: { target: { value: '' } }, value: 'bandKeyValue' });
			return;
		}

		const isPredefined = filteredBandOptions.some(opt => opt.value === value);

		if (isPredefined) {
			props.field_changehandler({ event: { target: { value: value } }, value: 'band' });
			props.field_changehandler({ event: { target: { value: '' } }, value: 'bandKeyValue' });
		} else {
			props.field_changehandler({ event: { target: { value: '' } }, value: 'band' });
			props.field_changehandler({ event: { target: { value: value } }, value: 'bandKeyValue' });
		}
	};

	// Grouped call letter dropdown handler
	const handleCallLetterSelectChange = (event) => {
		const value = event.target.value;

		if (!value) {
			// Reset — clear everything and go back to read-only
			setCallLetterManualMode(false);
			props.field_changehandler({ event: { target: { value: '' } }, value: 'callLetter' });
			props.field_changehandler({ event: { target: { value: '' } }, value: 'callLetterKeyValue' });
			return;
		}

		if (value === CALL_LETTER_MANUAL) {
			// "Enter Manually" selected — make input editable, clear any key-value mapping
			setCallLetterManualMode(true);
			props.field_changehandler({ event: { target: { value: '' } }, value: 'callLetterKeyValue' });
		} else {
			// Key Value Pairs group — store key name in callLetterKeyValue, resolved value will come from field_changehandler
			setCallLetterManualMode(false);
			props.field_changehandler({ event: { target: { value: '' } }, value: 'callLetter' });
			props.field_changehandler({ event: { target: { value: value } }, value: 'callLetterKeyValue' });
		}
	};

	// Manual text input handler for call letter
	const handleCallLetterInputChange = (event) => {
		props.field_changehandler({
			event: { target: { value: event.target.value } },
			value: 'callLetter'
		});
	};

	return (
		<div className="mf-12">
			<Box>
				{props.modelDefId === 3 && (
					<>
							<Grid container
								spacing={props.style.mainRowSpacing}
								sx={props.style.mainRowStyle}>
								<Grid item
									xs={props.style.cellOneWidth}
									sx={props.style.cellOneStyle}>
									<label>Call Letter</label>
								</Grid>
								<Grid item
									xs={props.style.cellTwoWidth}
									sx={props.style.cellTwoStyle}
									style={{ display: props.advanceEdit ? 'block' : 'none' }}>
									<select
										value={callLetterSelectValue}
										onChange={handleCallLetterSelectChange}
										disabled={props.readOnly}
										style={{ width: '100%', ...props.style.inputStyle }}
									>
										<option value="">Select Option</option>
										<optgroup label="Enter Manually">
														<option value={CALL_LETTER_MANUAL}>Manual Option</option>
													</optgroup>
										{sortedKeyValues.length > 0 && (
											<optgroup label="Key Value Pairs">
												{sortedKeyValues.map((kv, i) => (
													<option key={'kv-cl-' + i} value={kv.Key}>{kv.Key}</option>
												))}
											</optgroup>
										)}
									</select>
								</Grid>
								<Grid item
									xs={props.style.cellThreeWidth}
									sx={props.style.cellThreeStyle}>
									<input
										type="text"
													value={callLetterDisplayValue}
													readOnly={!callLetterManual || props.readOnly}
													onChange={handleCallLetterInputChange}
													maxLength={4}
													style={{ width: '100%', ...props.style.inputStyle }}
									/>
								</Grid>
							</Grid>

							<Grid container
								spacing={props.style.mainRowSpacing}
								sx={props.style.mainRowStyle}>
								<Grid item xs={props.style.cellOneWidth}
									sx={props.style.cellOneStyle}>
									<label>Media Type</label>
								</Grid>
								<Grid item
									xs={props.style.cellTwoWidth}
									sx={props.style.cellTwoStyle}>
									<select
										value={props.mappedFields['mediaType']?.value || ''}
										onChange={handleChange('mediaType')}
										disabled={props.readOnly}
										style={{ width: '100%', ...props.style.inputStyle }}
									>
										<option value="">Select Media Type</option>
										{mediaTypeOptions.map((opt) => (
											<option key={opt.value} value={opt.value}>{opt.label}</option>
										))}
									</select>
								</Grid>
							</Grid>

							<Grid container
								spacing={props.style.mainRowSpacing}
								sx={props.style.mainRowStyle}>
								<Grid item xs={props.style.cellOneWidth}
									sx={props.style.cellOneStyle}>
									<label>Band</label>
								</Grid>
								<Grid item
									xs={props.style.cellTwoWidth}
									sx={props.style.cellTwoStyle}
									style={{ display: props.advanceEdit ? 'block' : 'none' }}>
									<select
										value={bandSelectValue}
										onChange={handleBandChange}
										disabled={props.readOnly}
										style={{ width: '100%', ...props.style.inputStyle }}
									>
										<option value="">Select Band</option>
										{filteredBandOptions.filter(opt => opt.value !== '').length > 0 && (
											<optgroup label="Predefined Bands">
												{filteredBandOptions
													.filter(opt => opt.value !== '')
													.map(opt => (
														<option key={opt.value} value={opt.value}>{opt.label}</option>
													))}
											</optgroup>
										)}
										{sortedKeyValues.length > 0 && (
											<optgroup label="Key Value Pairs">
												{sortedKeyValues.map((kv, i) => (
													<option key={'kv-band-' + i} value={kv.Key}>{kv.Key}</option>
												))}
											</optgroup>
										)}
									</select>
								</Grid>
								<Grid item
									xs={props.style.cellThreeWidth}
									sx={props.style.cellThreeStyle}>
									<input
										type="text"
										readOnly
										value={bandDisplayValue}
										style={{ width: '100%', ...props.style.inputStyle }}
									/>
								</Grid>
							</Grid>


						</>
				)}
			</Box>
		</div>
	);
}


